import React, { useState } from "react";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import "../../Style/Components/select.scss";
import { BasketCard } from "../Card";
import ToggleInput from "./toggle-input";
import { PrimaryButton } from "../Cta";
import { useNavigate } from "react-router-dom";

export default function SelectBasket(props) {
  const navigate = useNavigate();
  const {
    options = [],
    prevSelected = new Map(), // Map<id, boolean> where presence means selected, value means recommended flag
    onPress,
    value,
    onChange,
    recommended,
    setRecommended,
    isClearable = true,
    placeholder = props.placeholder || "Select or create....",
    isMulti = true,
  } = props;

  // Local override map to remember user toggles for previously-selected items.
  // Key: string(id) -> boolean (true = selected, false = unselected)
  const [prevOverrides, setPrevOverrides] = useState(new Map());

  // Reset overrides if prevSelected reference changes (server updated)
  // useEffect(() => {
  //   setPrevOverrides(new Map());
  // }, [prevSelected]);

  // ---------- helpers to read prevSelected ----------
  const rawPrevHas = (rawKey) => {
    if (!prevSelected) return false;
    const kStr = String(rawKey);
    if (prevSelected instanceof Map)
      return prevSelected.has(kStr) || prevSelected.has(Number(rawKey));
    if (typeof prevSelected === "object") {
      return (
        Object.prototype.hasOwnProperty.call(prevSelected, rawKey) ||
        Object.prototype.hasOwnProperty.call(prevSelected, kStr) ||
        Object.prototype.hasOwnProperty.call(prevSelected, Number(rawKey))
      );
    }
    return false;
  };

  // const rawPrevGet = (rawKey) => {
  //   if (!prevSelected) return undefined;
  //   const kCandidates = [rawKey, String(rawKey), Number(rawKey)];
  //   for (const k of kCandidates) {
  //     if (prevSelected instanceof Map) {
  //       if (prevSelected.has(k)) return prevSelected.get(k);
  //     } else if (typeof prevSelected === "object") {
  //       if (Object.prototype.hasOwnProperty.call(prevSelected, k))
  //         return prevSelected[k];
  //     }
  //   }
  //   return undefined;
  // };

  // Treat presence in prevSelected as "previously selected"
  const prevExists = (rawKey) => Boolean(rawPrevHas(rawKey));

  // Whether prevSelected had it marked recommended
  // const prevWasRecommended = (rawKey) => Boolean(rawPrevGet(rawKey));

  // Effective previous selection considering local overrides:
  const effectivePrevSelected = (rawKey) => {
    const key = String(rawKey);
    if (prevOverrides.has(key)) return Boolean(prevOverrides.get(key));
    return prevExists(rawKey);
  };

  // ---------- normalize options ----------
  const normalizedOptions = (options || []).map((o, i) => {
    const uniqueValue =
      o?.fund_id ??
      o?.value ??
      (o?.fund_name ? `${o.fund_name}_${i}` : `opt_${i}`);
    const label = o?.fund_name ?? o?.label ?? o?.name ?? String(uniqueValue);
    return {
      ...o,
      // ensure basket_style_id remains if present; but keep a stable string `value` too
      value: String(o?.basket_style_id ?? uniqueValue),
      label,
    };
  });

  // getOptionValue returns string id (consistent)
  const getOptionValue = (opt) => {
    if (!opt) return "";
    if (opt.basket_style_id !== undefined && opt.basket_style_id !== null)
      return String(opt.basket_style_id);
    return String(opt.value ?? "");
  };
  const getOptionLabel = (opt) => opt?.label ?? "";

  // ---------- normalize incoming parent `value` into option objects ----------
  const normalizeIncomingValueToOptions = () => {
    if (!isMulti) {
      if (!value) return null;
      if (
        typeof value === "object" &&
        value !== null &&
        value.value !== undefined
      ) {
        return (
          normalizedOptions.find(
            (o) => getOptionValue(o) === getOptionValue(value),
          ) || value
        );
      }
      return (
        normalizedOptions.find((o) => getOptionValue(o) === String(value)) || {
          value: String(value),
          label: String(value),
        }
      );
    }

    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((v) => {
        if (v && typeof v === "object" && v.value !== undefined) {
          return (
            normalizedOptions.find(
              (o) => getOptionValue(o) === getOptionValue(v),
            ) || v
          );
        }
        if (v && typeof v === "object" && v.basket_style_id !== undefined) {
          const sid = String(v.basket_style_id);
          return (
            normalizedOptions.find((o) => getOptionValue(o) === sid) || {
              ...v,
              value: sid,
              label: v.name ?? v.label ?? sid,
            }
          );
        }
        // scalar id
        return (
          normalizedOptions.find((o) => getOptionValue(o) === String(v)) || {
            value: String(v),
            label: String(v),
          }
        );
      });
    }

    // fallback: single scalar provided but multi expected
    return [normalizeIncomingValueToOptions(value)];
  };

  const normalizedIncoming = normalizeIncomingValueToOptions();

  // ---------- merge effective prevSelected into displayedValue ----------
  let displayedValue;
  if (isMulti) {
    const existingIds = new Set((normalizedIncoming || []).map(getOptionValue));
    const additions = normalizedOptions.filter((o) => {
      const id = getOptionValue(o);
      return effectivePrevSelected(id) && !existingIds.has(id);
    });
    displayedValue = [...(normalizedIncoming || []), ...additions];
  } else {
    displayedValue = normalizedIncoming;
    if (
      !displayedValue ||
      (Array.isArray(displayedValue) && displayedValue.length === 0)
    ) {
      const prevEntry = normalizedOptions.find((o) =>
        effectivePrevSelected(getOptionValue(o)),
      );
      if (prevEntry) displayedValue = prevEntry;
    }
  }

  // ---------- detect parent expectation for output shape ----------
  const parentWantsObjects =
    Array.isArray(value) && value.length > 0 && typeof value[0] === "object";

  // ---------- helpers for updating overrides ----------
  const setOverrideFor = (nativeId, boolVal) => {
    const key = String(nativeId);
    const original = prevExists(nativeId);
    setPrevOverrides((prev) => {
      const next = new Map(prev);
      if (original === Boolean(boolVal)) {
        next.delete(key);
      } else {
        next.set(key, Boolean(boolVal));
      }
      return next;
    });
  };

  // ---------- handleChange ----------
  const handleChange = (newVal, actionMeta) => {
    // update prevOverrides using actionMeta.option (most reliable)
    try {
      const getNativeIdFromOption = (opt) => {
        if (!opt) return null;
        if (opt.basket_style_id !== undefined && opt.basket_style_id !== null)
          return opt.basket_style_id;
        return opt.value ?? null;
      };

      if (actionMeta && actionMeta.option) {
        const toggledNative = getNativeIdFromOption(actionMeta.option);
        // compute whether toggledNative is in newVal
        const currentlySelected = Array.isArray(newVal)
          ? newVal.some(
              (o) => String(getOptionValue(o)) === String(toggledNative),
            )
          : Boolean(
              newVal &&
              String(getOptionValue(newVal)) === String(toggledNative),
            );
        if (prevExists(toggledNative)) {
          setOverrideFor(toggledNative, currentlySelected);
        }
        // // if unchecking and this was recommended, clear recommended
        // if (!currentlySelected && (String(recommended) === String(toggledNative))) {
        //   setRecommended(null);
        // }
      }
    } catch (err) {
      // ignore override update errors
    }

    if (!onChange) return;

    if (!isMulti) {
      if (!newVal) {
        onChange(null);
        return;
      }
      if (parentWantsObjects) {
        onChange(newVal);
      } else {
        const out = newVal.basket_style_id ?? newVal.value ?? newVal;
        onChange(out);
      }
      return;
    }

    // multi
    const arr = Array.isArray(newVal) ? newVal : [];
    onChange(arr);
  };

  // ---------- custom option ----------
  const CheckboxOption = (optionProps) => {
    const { innerProps, data } = optionProps;
    // native id (prefer numeric basket_style_id)
    const nativeId =
      data &&
      data.basket_style_id !== undefined &&
      data.basket_style_id !== null
        ? data.basket_style_id
        : getOptionValue(data);
    const idKey = String(nativeId);
    const checkboxId = `select-checkbox-${idKey}`;

    const currentlySelected = Array.isArray(displayedValue)
      ? displayedValue.some((o) => getOptionValue(o) === idKey)
      : displayedValue && getOptionValue(displayedValue) === idKey;

    return (
      <components.Option {...optionProps} className="position-relative ">
        <input
          id={checkboxId}
          type="checkbox"
          checked={Boolean(currentlySelected)}
          aria-checked={Boolean(currentlySelected)}
          onChange={(e) => {
            e.stopPropagation();
            if (innerProps && typeof innerProps.onClick === "function") {
              innerProps.onClick(e);
            }
            // If user unchecks an item that was recommended, clear recommended
            if (String(recommended) === idKey) {
              setRecommended(null);
            }
          }}
          className="position-absolute z-2 e-select-basket-input"
        />

        <div
          style={{ right: "2rem", top: "1rem" }}
          className="position-absolute shadow-none d-flex"
          onClick={(e) => {
            e.stopPropagation();

            // allow toggling recommended for items that are selected now OR were previously selected
            if (currentlySelected) {
              if (String(data.basket_style_id) === String(recommended)) {
                setRecommended(null);
              } else {
                setRecommended(data.basket_style_id);
              }
              // toggle recommended: set native id (parent expects basket_style_id)
              // if (recommended !== null && String(recommended) === idKey) {
              //   setRecommended(null);
              // } else {
              //   setRecommended(nativeId);
              // }
            }
          }}
        >
          <ToggleInput
            label={"Recommended"}
            checked={
              String(data.basket_style_id) === String(recommended)
                ? true
                : false
            }
            type={3}
            onChange={() => {}}
            labelStyle={
              " e-primary-font-poppins e-font-10 e-font-weight-500 e-text-eerie-black"
            }
          />
        </div>

        <div onClick={(e) => e.stopPropagation()}>
          <BasketCard
            title={data.name ?? data.label}
            img={data.image}
            content={data.description}
            returns={`${data.return_percentage ?? ""} (${
              data.return_year ?? ""
            })`}
            bgColor="e-bg-magnolia e-pt-24px"
            borderColor="e-border-1px-languid-lavender"
            badges={data.features}
            handleView={() => {
              navigate(`/manage-basket/detail/${data.basket_style_id}`);
            }}
            NoAnimation={true}
          />
        </div>
      </components.Option>
    );
  };

  const InlineMenu = (menuProps) => {
    const { innerProps, innerRef, selectProps } = menuProps;
    return (
      <components.Menu {...menuProps}>
        <div
          {...innerProps}
          ref={innerRef}
          className="e-select-menu__menu-list row row-cols-2 g-3"
        >
          {menuProps.children}
        </div>
        <div className="d-flex justify-content-end align-items-center column-gap-2 pe-3 pt-3">
          <PrimaryButton
            ClassName="e-border-radius-pill e-border-1px-blue py-2 px-3 e-font-weight-500 e-font-16 e-w-fit bg-transparent e-text-blue-violet e-primary-font-poppins"
            name="Cancel"
            onPress={() => {
              onChange([]);
              selectProps.onMenuClose();
            }}
          />
          <PrimaryButton
            ClassName="e-border-radius-pill e-font-weight-500 py-2 e-font-16 e-w-fit e-bg-blue-violet text-white e-primary-font-poppins"
            name="Add basket"
            onPress={() => {
              onPress();
              onChange([]);
              selectProps.onMenuClose();
            }}
          />
        </div>
      </components.Menu>
    );
  };

  return (
    <div>
      <CreatableSelect
        isClearable={isClearable}
        isMulti={isMulti}
        defaultMenuIsOpen={true}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        className="e-select-basket"
        classNamePrefix="e-select-basket-menu"
        options={normalizedOptions}
        components={{
          Option: CheckboxOption,
          Menu: InlineMenu,
        }}
        value={displayedValue}
        onChange={handleChange}
        placeholder={placeholder}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
      />
    </div>
  );
}
